import { useState } from 'react';

import { type ReviewCycleResponse } from '../../api';
import { Form, FormError, Panel, TextField, useNotifications } from '../../components';
import { formatDate, formatDateTime, formatInteger } from '../../format';
import {
  useApproveReviewCycle,
  useCyclePreview,
  useReopenReviewCycle,
  useReviewDecisionsInFlight,
} from '../queries';
import { CyclePreviewViews } from './CyclePreviewViews';
import { REVIEW_COPY } from './reviewQueue';

/** The draft's build, which the page owns because decisions elsewhere on it start one. */
export interface PreviewBuild {
  /** Build the draft on the analyst's say-so. */
  readonly start: () => void;
  readonly isBuilding: boolean;
  readonly error: unknown;
  /** The draft on screen predates a decision whose rebuild failed. */
  readonly isStale: boolean;
}

/**
 * The gate: the file this cutoff would produce, and the button that signs it off.
 *
 * The whole point of the screen. The analyst builds the preview, reads what
 * moved against the last approved file -- who is new, who has gone, whose cells
 * changed -- or the file itself, line by line (see `CyclePreviewViews`), and
 * approves. Approving sends back the snapshot's identifier *and*
 * its digest, so if the preview was regenerated between reading it and pressing
 * the button the server refuses rather than recording a signature on a file
 * nobody read.
 *
 * Two states the screen has to make obvious rather than merely correct.
 * **Blocked**: while a movement in the cycle carries an impeditive flag the
 * button is disabled and says why, because the server's 409 is the right answer
 * and a wrong-looking one to arrive at by clicking. **Approved**: the cycle is
 * closed, its file is frozen, and the only thing on offer is reopening it --
 * which is recorded as an action of its own.
 *
 * **The draft follows the decisions.** Once a preview exists, every decision on
 * a movement rebuilds it (the page does that, see `ReviewQueuePage`), so what is
 * on screen is what the cutoff would now produce. While that rebuild is in
 * flight -- or after it failed -- the digest on screen is not the cycle's, and
 * the approve button says so by being disabled; the panel says so in words.
 */
export function CycleGatePanel({
  cycle,
  build,
}: {
  cycle: ReviewCycleResponse;
  build: PreviewBuild;
}) {
  const { notify } = useNotifications();
  const preview = useCyclePreview(cycle.contract_id, cycle.cycle_month);
  const decisionsInFlight = useReviewDecisionsInFlight();
  const approve = useApproveReviewCycle();
  const reopen = useReopenReviewCycle();

  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');

  const target = {
    contractId: cycle.contract_id,
    companyId: cycle.company_id,
    cycleMonth: cycle.cycle_month,
  };
  const built = preview.data ?? null;
  const exceptions = built?.snapshot.exception_count ?? 0;

  return (
    <Panel title={REVIEW_COPY.previewTitle} description={REVIEW_COPY.previewDescription}>
      <FormError error={build.error} />

      <div className="form__actions">
        <button
          type="button"
          className="button button--secondary"
          disabled={build.isBuilding || cycle.is_approved}
          onClick={build.start}
        >
          {built === null ? REVIEW_COPY.buildPreview : REVIEW_COPY.rebuildPreview}
        </button>
      </div>

      {built !== null && build.isBuilding && (
        <p className="panel__footnote" role="status">
          {REVIEW_COPY.previewUpdating}
        </p>
      )}
      {built !== null && !build.isBuilding && build.isStale && (
        <p className="panel__footnote" role="alert">
          <strong>{REVIEW_COPY.previewStale}</strong>
        </p>
      )}

      {built === null ? (
        <p className="panel__footnote">{REVIEW_COPY.needsPreview}</p>
      ) : (
        <>
          <dl className="definitions">
            <div>
              <dt>Data de corte</dt>
              <dd>{formatDate(cycle.cutoff_date)}</dd>
            </div>
            <div>
              <dt>Linhas no arquivo</dt>
              <dd>{formatInteger(built.snapshot.row_count)}</dd>
            </div>
            <div>
              <dt>Não renderizadas</dt>
              <dd>{formatInteger(exceptions)}</dd>
            </div>
            <div>
              <dt>Incluídas</dt>
              <dd>{formatInteger(built.comparison.added_count)}</dd>
            </div>
            <div>
              <dt>Removidas</dt>
              <dd>{formatInteger(built.comparison.removed_count)}</dd>
            </div>
            <div>
              <dt>Alteradas</dt>
              <dd>{formatInteger(built.comparison.changed_count)}</dd>
            </div>
          </dl>

          <p className="panel__footnote">
            Arquivo <code>{built.snapshot.file_name}</code> —{' '}
            <code>{built.snapshot.file_sha256}</code>
            {built.previous_snapshot_id === null
              ? ' · nenhum arquivo aprovado ainda: comparado com a população do fechamento anterior.'
              : ' · comparado com o último arquivo aprovado.'}
          </p>

          <CyclePreviewViews built={built} companyId={cycle.company_id} />
        </>
      )}

      <FormError error={approve.error} />

      {cycle.is_approved && cycle.approval !== null && cycle.approval !== undefined ? (
        <>
          <p className="panel__footnote">
            Ciclo aprovado por {cycle.approval.approved_by.display_name} em{' '}
            {formatDateTime(cycle.approval.approved_at)}, para o corte de{' '}
            {formatDate(cycle.approval.cutoff_date)}, sobre o arquivo{' '}
            <code>{cycle.approval.snapshot_sha256}</code>. Está pronto para envio e não muda mais.
          </p>
          <FormError error={reopen.error} />
          {reopening ? (
            <Form
              onSubmit={() =>
                reopen.mutate(
                  { ...target, reason },
                  {
                    onSuccess: () => {
                      setReopening(false);
                      setReason('');
                      notify('warning', REVIEW_COPY.reopened);
                    },
                  },
                )
              }
              aria-label={REVIEW_COPY.reopenCycle}
            >
              <TextField
                label={REVIEW_COPY.reasonLabel}
                hint="O ciclo estava pronto para envio e deixou de estar; isso fica registrado."
                required
                value={reason}
                onChange={setReason}
              />
              <div className="form__actions">
                <button type="submit" className="button button--danger">
                  {REVIEW_COPY.reopenCycle}
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => setReopening(false)}
                >
                  Cancelar
                </button>
              </div>
            </Form>
          ) : (
            <div className="form__actions">
              <button
                type="button"
                className="button button--secondary"
                onClick={() => setReopening(true)}
              >
                {REVIEW_COPY.reopenCycle}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="form__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={
                built === null ||
                cycle.blocking_count > 0 ||
                approve.isPending ||
                // The digest on screen is about to change, or no longer
                // matches the cycle: approving it would sign a file that is
                // not the one the cutoff produces.
                build.isBuilding ||
                build.isStale ||
                decisionsInFlight > 0
              }
              onClick={() => {
                if (built === null) {
                  return;
                }
                approve.mutate(
                  {
                    ...target,
                    snapshotId: built.snapshot.id,
                    snapshotSha256: built.snapshot.file_sha256,
                    acknowledgeExceptions: exceptions > 0,
                  },
                  { onSuccess: () => notify('success', REVIEW_COPY.approved) },
                );
              }}
            >
              {REVIEW_COPY.approveCycle}
            </button>
          </div>
          {cycle.blocking_count > 0 && (
            <p className="panel__footnote">{REVIEW_COPY.blockedFromApproval}</p>
          )}
          {exceptions > 0 && (
            <p className="panel__footnote">
              {formatInteger(exceptions)} pessoa(s) não puderam ser renderizadas. Aprovar assim
              envia um arquivo que não descreve toda a população — e isso fica registrado como
              decisão sua.
            </p>
          )}
        </>
      )}
    </Panel>
  );
}
